import jsPDF, {TextOptionsLight} from 'jspdf';
import {FontStyle} from 'jspdf-autotable';
import moment, {MomentInput} from 'moment';
import {PDF_FONT_MAIN_COLOR} from './export-pdf-font';

/**
 * Draws a circular image on the PDF by clipping the image with a circle path
 */
export function pdfAddCircularImage(
  pdf: jsPDF,
  imageData: string,
  x: number,
  y: number,
  diameter: number
) {
  const radius = diameter / 2;
  const centerX = x + radius;
  const centerY = y + radius;

  // Save the current graphics state
  pdf.saveGraphicsState();

  // Create a circular clipping path
  pdf.clip();
  pdf.circle(centerX, centerY, radius, 'S');

  // Draw the image
  pdf.addImage(imageData, 'JPEG', x, y, diameter, diameter);

  // Restore the graphics state
  pdf.restoreGraphicsState();
}

export function pdfAddCircularInitialsAvatar(
  pdf: jsPDF,
  initials: string,
  x: number,
  y: number,
  diameter: number,
  opts?: {
    backgroundColor?: [number, number, number];
    textColor?: [number, number, number];
  }
) {
  const radius = diameter / 2;
  const centerX = x + radius;
  const centerY = y + radius;

  pdf.saveGraphicsState();
  pdf.setFillColor(...(opts?.backgroundColor ?? [189, 189, 189]));
  pdf.circle(centerX, centerY, radius, 'F');

  pdf.setTextColor(...(opts?.textColor ?? [255, 255, 255]));
  pdf.setFont('Helvetica', 'normal');
  pdf.setFontSize(Math.max(16, diameter * 0.42));
  pdf.text(initials, centerX, centerY + diameter * 0.12, {align: 'center'});
  pdf.restoreGraphicsState();
}

/** General function to add a labeled field to the PDF */
export function pdfAddField(pdf: jsPDF, label: string, x: number, y: number): void;
export function pdfAddField(
  pdf: jsPDF,
  label: string,
  text: string | undefined,
  x: number,
  y: number
): void;
export function pdfAddField(
  pdf: jsPDF,
  label: string,
  textOrX: string | number | undefined,
  xOrY: number,
  y?: number
) {
  const posX = typeof textOrX === 'number' ? textOrX : xOrY;
  const posY = typeof textOrX === 'number' ? xOrY : y!;
  const val = typeof textOrX === 'number' ? undefined : textOrX;

  // Label
  const fieldLabel = label + (val ? ': ' : '');
  pdfAddText(pdf, fieldLabel, posX, posY, {font: {style: 'bold'}});
  const labelWidth = pdf.getTextWidth(fieldLabel); // get width in bold font.

  // Value
  if (val) {
    pdfAddText(pdf, val, posX + labelWidth, posY);
  }

  // Reset global changes
  pdf.setFont('Helvetica', 'normal');
}

/** General function to add text to the PDF */
export function pdfAddText(
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  opts?: {
    font?: {
      name?: string;
      style?: FontStyle;
      size?: number;
      color?: string;
    };
    textOptions?: TextOptionsLight;
  }
) {
  if (opts?.font?.size) pdf.setFontSize(opts.font.size);
  if (opts?.font?.color) pdf.setTextColor(opts.font.color);
  pdf.setFont(opts?.font?.name ?? 'Helvetica', opts?.font?.style ?? 'normal');
  pdf.text(text, x, y, opts?.textOptions);
}

/**  General function to add a horizontal line to the PDF */
export function pdfAddHorizontalLine(pdf: jsPDF, margin: number, pos: number): number {
  pdf.setDrawColor(228, 227, 227); // #E4E3E3
  pdf.setLineWidth(0.1);
  pdf.line(margin, pos, pdf.internal.pageSize.width - margin, pos);

  return pos;
}

export function pdfAddBodyTitle(
  pdf: jsPDF,
  content: {
    title: string;
    subtitle?: string;
  },
  opts?: {
    x?: number;
    y?: number;
    fontSize?: number;
    gapAfter?: number;
    subtitleFontSize?: number;
    subtitleGap?: number;
  }
): number {
  const titleX = opts?.x ?? 14;
  const titleY = opts?.y ?? 24;
  const fontSize = opts?.fontSize ?? 14;
  const gapAfter = opts?.gapAfter ?? 3;
  const title = content.title.trim();
  const subtitle = content.subtitle?.trim();
  const subtitleFontSize = opts?.subtitleFontSize ?? 9.5;
  const subtitleGap = opts?.subtitleGap ?? 0.9;

  pdf.setTextColor(PDF_FONT_MAIN_COLOR);
  pdf.setFont('Helvetica', 'bold');
  pdf.setFontSize(fontSize);
  pdf.text(title, titleX, titleY, {baseline: 'top'});

  const textHeight = pdf.getTextDimensions(title).h;

  // Only title, no subtitle
  if (!subtitle) {
    pdf.setFont('Helvetica', 'normal');

    return titleY + textHeight + gapAfter + 1;
  }

  // With subtitle
  const subtitleY = titleY + textHeight + subtitleGap;
  const subtitleHeight = pdf.getTextDimensions(subtitle).h;

  pdf.setFont('Helvetica', 'normal');
  pdf.setFontSize(subtitleFontSize);
  pdf.text(subtitle, titleX, subtitleY, {baseline: 'top'});
  pdf.setFont('Helvetica', 'normal');

  return subtitleY + subtitleHeight + gapAfter;
}

export function formatPdfDateRangeTitle({
  startDate,
  endDate,
}: {
  startDate: MomentInput;
  endDate: MomentInput;
}): string {
  const from = moment(startDate);
  const to = moment(endDate);

  if (!from.isValid() || !to.isValid()) {
    return '';
  }

  return `${capitalizeDateLabel(from.format('DD MMM YY'))} - ${capitalizeDateLabel(
    to.format('DD MMM YY')
  )}`;
}

function capitalizeDateLabel(value: string): string {
  return value
    .split(' ')
    .map((part) => (part ? `${part.charAt(0).toUpperCase()}${part.slice(1)}` : part))
    .join(' ');
}
